import { useEffect, useMemo, type CSSProperties } from 'react';
import { useUiLocale } from '../../../app/ui-locale';
import type { FormField, FormValues } from '../../../models/form';
import { evaluateCalculation, formatCalculation } from '../../../utils/calculation';
import { publicCopy } from '../public-copy';

interface Props {
  field: FormField;
  tabName: string;
  values: FormValues;
  value: unknown;
  readOnly: boolean;
  controlId: string;
  className: string;
  style?: CSSProperties;
  attributes: Record<string, string | boolean | number>;
  describedBy?: string;
  onChange: (value: unknown) => void;
}

export function CalculatedFieldInput({ field, tabName, values, value, readOnly, controlId, className, style, attributes, describedBy, onChange }: Props) {
  const { locale } = useUiLocale();
  const copy = publicCopy(locale);
  const calculation = useMemo(() => {
    try {
      const result = evaluateCalculation(field.calculationFormula, values, tabName);
      const decimals = Math.max(0, Math.min(8, Math.trunc(field.calculationDecimals ?? 0)));
      const canonical = result.toFixed(decimals);
      const display = formatCalculation(result, field.calculationFormat ?? 'number', decimals, field.calculationCurrencyLabel ?? copy.defaultCurrency);
      return { result, canonical, display, error: '' };
    } catch {
      return { result: 0, canonical: '', display: copy.invalidFormula, error: copy.invalidFormulaMessage };
    }
  }, [copy, field.calculationCurrencyLabel, field.calculationDecimals, field.calculationFormat, field.calculationFormula, tabName, values]);

  useEffect(() => {
    if (readOnly || calculation.error) return;
    if (String(value ?? '') !== calculation.canonical) onChange(calculation.canonical);
  }, [calculation.canonical, calculation.error, onChange, readOnly, value]);

  if (readOnly) {
    return <div className={`df-readonly-value df-calculated-value ${calculation.error ? 'has-error' : ''}`} title={calculation.error || field.calculationFormula}>{calculation.display}</div>;
  }

  return <div className={`df-calculated-control ${calculation.error ? 'has-error' : ''}`} title={calculation.error || field.calculationFormula}>
    <input
      {...attributes}
      id={controlId}
      name={field.name}
      className={className}
      style={style}
      type="text"
      value={calculation.display}
      readOnly
      aria-readonly="true"
      aria-invalid={Boolean(calculation.error)}
      aria-describedby={describedBy}
      tabIndex={field.tabIndex}
    />
    <span className="df-calculated-badge" aria-label={copy.calculatedValue}>ƒx</span>
    {calculation.error ? <small className="help-block">{calculation.error}</small> : null}
  </div>;
}
